import React, { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { useLocale } from "@/contexts/LocaleContext";

const NUMBER = "43544521006";

const isMobileDevice = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // iPad reports as desktop on iOS 13+ → behandeln wie Tablet (Chat-Panel)
  return /Android|iPhone|iPod|Mobile|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua);
};

const StickyWhatsApp: React.FC = () => {
  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleButtonClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (isMobileDevice()) {
      window.open(`https://wa.me/${NUMBER}`, "_blank", "noopener,noreferrer");
      return;
    }
    setOpen((v) => !v);
  };

  const handleSend = () => {
    const text = message.trim();
    const url = text
      ? `https://web.whatsapp.com/send/?phone=${NUMBER}&text=${encodeURIComponent(text)}&type=phone_number&app_absent=0`
      : `https://web.whatsapp.com/send/?phone=${NUMBER}&text&type=phone_number&app_absent=0`;
    window.open(url, "_blank", "noopener,noreferrer");
    setMessage("");
    setOpen(false);
  };

  useEffect(() => {
    if (open) {
      const id = window.setTimeout(() => textareaRef.current?.focus(), 50);
      return () => window.clearTimeout(id);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <div style={{ bottom: "var(--whatsapp-bottom, 1rem)" }} className="fixed right-4 sm:right-6 z-50">
      {open && (
        <div
          role="dialog"
          aria-label={t("whatsapp.title")}
          className="absolute bottom-[calc(100%+12px)] right-0 w-[320px] sm:w-[360px] max-w-[calc(100vw-2rem)] rounded-xl bg-[#F2F2F2] shadow-2xl border border-black/5 overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-200"
        >
          <div className="flex items-center justify-between bg-[#6C184F] text-white px-4 py-3">
            <span className="font-display uppercase tracking-[0.15em] text-[13px]">
              {t("whatsapp.title")}
            </span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label={t("whatsapp.close")}
              className="inline-flex h-7 w-7 items-center justify-center rounded hover:bg-white/10 transition-colors"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>

          <div className="px-4 pt-4">
            <div className="bg-white text-[#383637] text-[14px] leading-[1.5] rounded-lg px-3 py-2 shadow-sm">
              {t("whatsapp.greeting")}
            </div>
          </div>

          <div className="p-4 pt-3">
            <textarea
              ref={textareaRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder={t("whatsapp.placeholder")}
              rows={3}
              className="w-full resize-none rounded-md border border-black/10 bg-white px-3 py-2 text-[14px] text-[#383637] placeholder:text-[#A59FA1] focus:outline-none focus:ring-2 focus:ring-[#6C184F]"
            />
            <button
              type="button"
              onClick={handleSend}
              className="mt-3 inline-flex items-center justify-center rounded-full bg-[#6C184F] px-5 h-[37px] text-white text-[13px] font-display uppercase tracking-[0.15em] hover:opacity-90 transition-opacity w-full"
            >
              {t("whatsapp.send")}
            </button>
            <p className="mt-2 text-[11px] text-[#605A5C] text-center">{t("whatsapp.hint")}</p>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={handleButtonClick}
        aria-label={t("whatsapp.aria")}
        aria-expanded={open}
        className="block transition-transform duration-300 hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6C184F] rounded-full"
      >
        <svg
          viewBox="0 0 71 71"
          width={71}
          height={71}
          aria-hidden="true"
          focusable="false"
          className="h-14 w-14 sm:h-[71px] sm:w-[71px] drop-shadow-[0_3px_3px_rgba(0,0,0,0.2)]"
        >
          <circle cx="35.5" cy="32.5" r="32.5" fill="#F2F2F2" />
          <path d="M35.5 0C17.5535 0 3 14.5535 3 32.5C3 50.4465 17.5535 65 35.5 65C53.4465 65 68 50.4465 68 32.5C68 14.5535 53.4465 0 35.5 0ZM36.1888 51.4225C36.1883 51.4225 36.1893 51.4225 36.1888 51.4225H36.1809C32.9252 51.421 29.7261 50.6047 26.885 49.0555L16.5736 51.7597L19.3333 41.6828C17.6309 38.7341 16.7352 35.3887 16.7367 31.9614C16.7412 21.2399 25.4672 12.5173 36.1888 12.5173C41.3919 12.5193 46.2761 14.5446 49.9484 18.2198C53.6211 21.8954 55.6424 26.7812 55.6404 31.9768C55.636 42.6989 46.9089 51.4225 36.1888 51.4225Z" fill="#6C184F" />
          <path d="M36.287 15.5999C27.3953 15.5999 20.164 22.828 20.16 31.7129C20.159 34.7576 21.0115 37.7228 22.6249 40.2884L23.0081 40.898L21.3793 46.8446L27.4803 45.2446L28.0692 45.5936C30.5439 47.0621 33.3811 47.8389 36.2736 47.8399H36.28C45.1648 47.8399 52.3961 40.6112 52.4 31.7258C52.4015 27.4198 50.7263 23.3714 47.683 20.3256C44.6397 17.2799 40.5921 15.6013 36.287 15.5999ZM45.7685 38.6409C45.3645 39.7722 43.4288 40.8051 42.4977 40.9445C41.6626 41.0691 40.6065 41.121 39.4455 40.7526C38.7414 40.5292 37.839 40.231 36.6825 39.7321C31.8216 37.6338 28.6467 32.7409 28.4045 32.4175C28.1622 32.0941 26.4257 29.7906 26.4257 27.4059C26.4257 25.0218 27.6776 23.8495 28.1216 23.3649C28.5661 22.8799 29.0913 22.7587 29.4141 22.7587C29.737 22.7587 30.0604 22.7617 30.3427 22.7756C30.6404 22.7904 31.0399 22.6623 31.433 23.6072C31.8369 24.5773 32.806 26.9614 32.9272 27.2037C33.0483 27.4465 33.1289 27.7293 32.9677 28.0526C32.806 28.376 32.2691 29.0736 31.7563 29.7095C31.5412 29.976 31.2609 30.2133 31.5437 30.6984C31.826 31.1829 32.7991 32.7695 34.2394 34.0541C36.0907 35.7045 37.6521 36.2157 38.1367 36.4585C38.6207 36.7008 38.9036 36.6602 39.1864 36.3374C39.4687 36.014 40.3978 34.9228 40.7207 34.4377C41.0435 33.9527 41.3669 34.0338 41.8109 34.1955C42.2554 34.3567 44.6377 35.529 45.1222 35.7712C45.6068 36.014 45.9297 36.1351 46.0508 36.3374C46.1725 36.5396 46.1725 37.5092 45.7685 38.6409Z" fill="#6C184F" />
        </svg>
      </button>
    </div>
  );
};

export default StickyWhatsApp;
